import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useEffect, useState } from 'react';

interface MediaPost {
  id: string;
  title: string | null;
  description: string | null;
  media_url: string;
  media_type: string;
}

export const MediaGallery = ({ userId }: { userId: string }) => {
  const [mediaUrls, setMediaUrls] = useState<Record<string, string>>({});

  const { data: mediaPosts, isLoading } = useQuery({
    queryKey: ['userMedia', userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_media_posts')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as MediaPost[];
    },
  });

  useEffect(() => {
    const loadMediaUrls = async () => {
      if (!mediaPosts) return;
      
      const urls: Record<string, string> = {};
      for (const post of mediaPosts) {
        const { data } = await supabase.storage
          .from('user_media')
          .getPublicUrl(post.media_url);
        urls[post.id] = data.publicUrl;
      }
      setMediaUrls(urls);
    };

    loadMediaUrls();
  }, [mediaPosts]);

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-48 w-full" />
        ))}
      </div>
    );
  }

  if (!mediaPosts?.length) {
    return (
      <div className="text-center py-8 text-gray-500">
        No media posts yet
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {mediaPosts.map((post) => (
        <Card key={post.id} className="overflow-hidden">
          <CardContent className="p-0">
            {post.media_type.startsWith('image/') ? (
              <img
                src={mediaUrls[post.id]}
                alt={post.title || 'User media'}
                className="w-full h-48 object-cover"
              />
            ) : post.media_type.startsWith('video/') ? (
              <video
                src={mediaUrls[post.id]}
                controls
                className="w-full h-48 object-cover"
              />
            ) : null}
            {(post.title || post.description) && (
              <div className="p-4">
                {post.title && (
                  <h3 className="font-semibold mb-1">{post.title}</h3>
                )}
                {post.description && (
                  <p className="text-sm text-gray-600">{post.description}</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};